import { getRouteApi, useNavigate, useSearch } from '@tanstack/react-router';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TSorting } from './types';

import { useSearch as usePhraseSearch } from 'helpers/search';

type TPagination = {
  page: number;
  results: number;
};

export const useTableFilters = <
  T extends Record<string, Array<boolean | string>>,
>(
  initialState: T,
  options?: {
    /**
     * Provide custom `useSearch()` response. This is useful when we want
     * to parse specific parameters.
     */
    search?: ReturnType<typeof useSearch>;
    setPagination?: ReturnType<typeof useTablePagination>[1];
    useQueryParams?: true;
  },
) => {
  const setPagination = options?.setPagination;
  const useQuery = options?.useQueryParams;

  const navigate = useNavigate();
  const _search = useSearch({
    strict: false,
  });
  const search = options?.search ?? _search;
  const refInitialState = useRef(initialState);
  const [state, setState] = useState(refInitialState.current);

  useEffect(() => {
    if (!useQuery) {
      return;
    }

    const searchState = Object.keys(refInitialState.current).reduce(
      (acc, curr) => {
        const filters = (search as T)[curr];
        if (filters) {
          // A single-value filter round-trips through the URL as a bare
          // scalar (qs arrayFormat 'repeat'), and not every route schema
          // coerces every key — normalize to the array shape the initial
          // state declares so filter dialogs can safely .map. Keys whose
          // initial state is deliberately non-array pass through untouched.
          // @ts-ignore
          acc[curr] =
            Array.isArray(refInitialState.current[curr]) &&
            !Array.isArray(filters)
              ? [filters]
              : filters;
        }
        return acc;
      },
      {} as T,
    );
    const initialState = {
      ...refInitialState.current,
      ...searchState,
    };
    setStaged(initialState);
    setState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [staged, setStaged] = useState(state);

  const getCount = useCallback(
    (a: T) =>
      Object.values(a).reduce(
        (sum, filters) =>
          sum +
          (Array.isArray(filters)
            ? filters.filter((x) => x !== 'all').length
            : 0),
        0,
      ),
    [],
  );

  const getDirtyCount = useCallback(
    (a: T, b: T) =>
      Object.entries(a)
        .map(
          ([key, value]) =>
            b[key].length !== value.length ||
            b[key].some((x) => !value.includes(x)),
        )
        .filter(Boolean).length,
    [],
  );

  const getCountStaged = useCallback(
    () => getCount(staged),
    [getCount, staged],
  );

  const getCountState = useCallback(() => getCount(state), [getCount, state]);

  const getHasDirtyStaged = useCallback(
    () => getDirtyCount(state, staged) > 0,
    [getDirtyCount, state, staged],
  );

  const getHasDirtyState = useCallback(
    () => getDirtyCount(refInitialState.current, state) > 0,
    [getDirtyCount, state],
  );

  const setFilters: typeof setState = useCallback(
    (updater) => {
      if (useQuery) {
        navigate({
          search: (prev) => ({
            ...prev,
            ...(typeof updater === 'function' ? updater(prev as T) : updater),
          }),
        });
      }

      setState(updater);

      setPagination?.((value) => ({
        ...value,
        pageIndex: 0,
      }));
    },
    [navigate, setPagination, useQuery],
  );

  const clear = useCallback(() => {
    // empty all arrays
    const emptyFilters = { ...refInitialState.current };
    Object.entries(emptyFilters).forEach(([key, value]) => {
      let v = value as T[keyof T];
      if (Array.isArray(v)) {
        v = [] as unknown as T[keyof T];
      }
      emptyFilters[key as keyof T] = v;
    });
    setStaged(emptyFilters);
    setFilters(emptyFilters);
  }, [setFilters]);

  const commit = useCallback(() => {
    setFilters(staged);
  }, [setFilters, staged]);

  const reset = useCallback(() => {
    setStaged(refInitialState.current);
    setFilters(refInitialState.current);
  }, [setFilters]);

  const resetStaged = useCallback(() => {
    setStaged(state);
  }, [state]);

  return {
    clear,
    commit,
    getCountStaged,
    getCountState,
    getHasDirtyStaged,
    getHasDirtyState,
    reset,
    resetStaged,
    setStaged,
    setState: setFilters,
    staged,
    state,
  };
};

const route = getRouteApi('/_private');

export const useTablePagination = <
  TResults extends number,
  TInitialState extends {
    page?: number;
    results?: TResults;
  },
>(
  initialState?: TInitialState,
  options?: {
    useQueryParams?: true;
  },
) => {
  const useQuery = options?.useQueryParams;

  const [state, setState] = useState({
    page: initialState?.page ?? 1,
    results: initialState?.results ?? 100,
  });
  const navigate = useNavigate();
  const search = route.useSearch();
  const query = useMemo(
    () => ({
      page: search.page ?? state.page,
      results: search.results ?? state.results,
    }),
    [search.page, search.results, state.page, state.results],
  );

  const setPagination: React.Dispatch<React.SetStateAction<PaginationState>> =
    useCallback(
      (updater) => {
        const handler = (prevState: TPagination): TPagination => {
          const result =
            typeof updater === 'function'
              ? updater({
                  pageIndex: prevState.page - 1,
                  pageSize: prevState.results,
                })
              : updater;
          const nextState: Partial<TPagination> = {};
          if (result?.pageIndex !== undefined) {
            nextState.page = result.pageIndex + 1;
          }
          if (result?.pageSize !== undefined) {
            nextState.results = result.pageSize;
          }
          return nextState as TPagination;
        };

        if (useQuery) {
          navigate({
            search: (prev) => ({
              ...prev,
              ...handler(prev as typeof query),
            }),
          });
        } else {
          setState(handler);
        }
      },
      [navigate, useQuery],
    );

  const response = useQuery ? query : state;

  const pagination = useMemo(
    () => ({
      pageIndex: response.page - 1,
      pageSize: response.results,
    }),
    [response.page, response.results],
  );

  return [pagination, setPagination] as [PaginationState, typeof setPagination];
};

export const useTableSearch = (options?: {
  onSearch?: (phrase: string) => void;
  setPagination?: ReturnType<typeof useTablePagination>[1];
}) => {
  const setPagination = options?.setPagination;

  const searchState = usePhraseSearch({ onSearch: options?.onSearch });

  useEffect(() => {
    if (!searchState.debouncedPhrase) {
      return;
    }

    setPagination?.((value) => ({
      ...value,
      pageIndex: 0,
    }));
  }, [searchState.debouncedPhrase, setPagination]);

  return searchState;
};

// TODO: Add multi-column sorting to the hook
export const useTableSorting = <T extends string>(
  initialState: {
    order?: 'asc' | 'desc';
    sort?: T;
  },
  options?: {
    useQueryParams?: true;
  },
) => {
  const useQuery = options?.useQueryParams;

  const navigate = useNavigate();
  const search = route.useSearch();

  const query = useMemo(
    () => ({
      order: search.order ?? initialState.order,
      sort: (search.sort as T | undefined) ?? initialState.sort,
    }),
    [initialState.order, initialState.sort, search.order, search.sort],
  );
  const [state, setState] = useState(initialState);

  const setSorting = useCallback(
    (updater?: React.SetStateAction<SortingState> | SortingState) => {
      // Define a custom updater that we'll pass to setState (or setQuery).
      // Necessary because react-table expects a sort object like { id, desc },
      // but we store our state object as { order, sort }.
      const handler = (prevState: typeof state) => {
        const resolvePrevState = [
          {
            desc: prevState.order === 'desc',
            id: prevState.sort ?? '',
          },
        ];

        const result =
          typeof updater === 'function' ? updater(resolvePrevState) : updater;

        // Create an empty nextState object that will be returned if the above didn't resolve
        // to something meaningful.
        const nextState: typeof state = {
          order: undefined,
          sort: undefined,
        };

        // Translate the order and sort values from the result if present.
        if (result && result[0]) {
          nextState.order = result[0].desc ? 'desc' : 'asc';
          nextState.sort = result[0].id as T;
        }

        return nextState;
      };

      // Call setQuery/setState with our custom handler.
      if (useQuery) {
        navigate({
          search: (prev) => ({
            ...prev,
            ...handler(prev as typeof query),
          }),
        });
      } else {
        setState(handler);
      }
    },
    [navigate, setState, useQuery],
  );

  const response = useQuery ? (query as typeof state) : state;

  const sorting = useMemo(
    () =>
      response.sort
        ? [
            {
              desc: response.order === 'desc',
              id: response.sort,
            },
          ]
        : [],
    [response.sort, response.order],
  );

  return [sorting, setSorting] as [TSorting<T>, typeof setSorting];
};
